/**
 * /subscription — основной раздел подписки.
 *
 * Per владелец 2026-05-20: подписка = ОТДЕЛЬНАЯ страница, не интегрирована
 * в Profile как огромный блок. В Profile есть только кнопка
 * «Управління підпискою» → ведёт сюда.
 *
 * Открывается также из:
 * - Home → subscription card
 * - Feed → FeedUpsellBanner
 * - FeedVacancy → ContactLimitModal CTA
 *
 * Промо НЕ показываются в UI — они приватные ссылки (только активация
 * через deeplink в боте).
 */

import { useEffect, useRef, useState } from "react";
import { MAX_BONUS_PERCENT, MAX_BONUS_SHARE } from "@/lib/bonuses";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { BackNav } from "@/components/BackNav";
import { PageHeader } from "@/components/PageHeader";
import { Icon, type IconName } from "@/components/Icon";
import { CenteredError, Spinner } from "@/components/Spinner";
import {
  useAutocharge,
  useCancelRecurring,
  useCreateInvoice,
  useFreezeInvoice,
  useFreezeTariffs,
  useFreezeUseEarned,
  useGiftWindow,
  useKBCounts,
  useMySubscription,
  useReferrals,
  useMarkGiftOpened,
  useTariffs,
} from "@/api/hooks";
import { TgEmoji } from "@/components/TgEmoji";
// Головна кнопка дії. Локальна `ActionButton` нижче — це рядок-кнопка
// управління підпискою, тому імпортуємо під іншим іменем.
import { ActionButton as MainButton } from "@/components/ActionButton";
import {
  RetentionOfferBanner,
} from "@/components/RetentionOffer";
import { openRetentionOffer } from "@/lib/retentionOffer";
import { GiftOfferModal } from "@/components/GiftOfferModal";
import type { AutochargeOut, GiftOfferOut } from "@/api/hooks";
import { queryKeys } from "@/api/hooks";
import { ApiError } from "@/api/client";
import { useQueryClient } from "@tanstack/react-query";
import { Collapse } from "@/components/Collapse";
import { Section } from "@/components/Section";
import {
  SUBSCRIPTION_BENEFITS,
  benefitDesc,
  kbCountsFrom,
} from "@/lib/benefits";
import type { SubscriptionOut, TariffOut } from "@/api/types";
import { isActiveSubscription } from "@/api/types";
import { formatAmount, formatDate } from "@/lib/utils";
import { tg } from "@/lib/telegram";
import { showAppAlert } from "@/lib/appAlert";

const BEST_DEAL_SLUG = "standart_3m";

/**
 * Перед створенням рахунку зі знижкою тягнемо СВІЖИЙ баланс бонусів
 * (owner 2026-08-10). Кешу 30 с вистачало, щоб показати знижку з бонусів, яких
 * уже немає: людина встигала обміняти їх на дні в іншому екрані. Сервер знижку
 * все одно перерахує по реальному балансу — але людина має бачити правду ДО
 * оплати, а не дізнатись про іншу суму на сторінці банку.
 *
 * Повертає true, якщо знижку ще можна застосувати.
 */
async function ensureFreshBonusBalance(
  refetch: () => Promise<{ data?: { regular_balance_uah?: number } }>,
  shown: number,
  onDrop: (fresh: number) => void,
): Promise<boolean> {
  try {
    const fresh = (await refetch()).data?.regular_balance_uah ?? 0;
    if (fresh >= shown) return fresh > 0;
    onDrop(fresh);
    if (fresh <= 0) {
      showAppAlert("Бонуси вже витрачені — рахунок буде без знижки.");
      return false;
    }
    showAppAlert(`Бонусів лишилось ${fresh} ₴ — знижка буде меншою.`);
    return true;
  } catch {
    // Не змогли перевірити — не блокуємо оплату, сервер порахує сам.
    return true;
  }
}

/** Скільки днів подарунка дасть саме цей тариф. 0 — умова не виконана. */
/** Людина прийшла за кнопкою з попапа офера: `/subscription?offer=<ключ>`.
 *
 * Owner 2026-09-05: «офери мають бути доступні лише по попапу». У звичайній
 * сітці тарифів подарунка немає — там працюють бонуси. І навпаки: у режимі
 * офера бонуси не застосовуються (сервер приймає їх лише на повну ціну).
 */
function offerFromUrl(): string | null {
  try {
    return new URLSearchParams(window.location.search).get("offer");
  } catch {
    return null;
  }
}

/** Яку панель керування відкрити одразу: `/subscription?panel=freeze`. */
function panelFromUrl(): string | null {
  try {
    return new URLSearchParams(window.location.search).get("panel");
  } catch {
    return null;
  }
}

function giftDaysFor(gift: GiftOfferOut | undefined, tariff: TariffOut): number {
  if (!gift?.bonus_days) return 0;
  if (tariff.slug === "test") return 0;
  if (tariff.days < (gift.min_tariff_days ?? 28)) return 0;
  // Верхня межа: акція «45 днів замість 30» — саме про місяць, і плашка не
  // має світитись на 3/6/12 місяцях (owner 2026-09-01).
  const max = gift.max_tariff_days;
  if (max != null && tariff.days > max) return 0;
  return gift.bonus_days;
}

/** Плашка подарунка — ОДИН В ОДИН із банером спецпропозиції.
 *
 * Owner 2026-08-11: «нет какой-то общей стилистики». Своя картка (спершу
 * зелена, потім помаранчева) щоразу виглядала чужою поруч із рештою екрана.
 * Тому не вигадуємо нову: беремо готовий патерн `RetentionOfferBanner`
 * (біла картка + теплий glow + плитка з емодзі + пілюля таймера) і лише
 * підставляємо свій текст. Будь-який наступний офер робити так само.
 */
export function GiftBanner({
  gift,
  onOpen,
}: {
  gift: GiftOfferOut;
  onOpen: () => void;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  const left = new Date(gift.expires_at ?? 0).getTime() - now;
  if (left <= 0) return null;
  const h = Math.floor(left / 3_600_000);
  const m = Math.floor((left % 3_600_000) / 60_000);
  // Поки більше 72 годин — днями, далі годинами (owner 2026-08-11).
  const leftText =
    h >= 72 ? `${Math.ceil(h / 24)} дн.` : h >= 1 ? `${h} год` : `${m} хв`;
  const bonus = gift.bonus_days ?? 0;
  // Той самий поділ, що й у попапі: подарунок днями / знижка / вхід за 1 ₴.
  // Без нього плашка називала «14 днів за 1 ₴» знижкою 55% (owner 2026-08-11).
  const kind = gift.kind ?? (bonus > 0 ? "days" : "discount");

  return (
    <button
      type="button"
      onClick={() => {
        tg()?.HapticFeedback?.impactOccurred?.("medium");
        onOpen();
      }}
      className="relative w-full overflow-hidden rounded-ios-card bg-surface p-4 mb-3 text-left shadow-card border border-primary/25 active:scale-[0.99] transition"
    >
      <div
        className="pointer-events-none absolute -top-8 -right-8 h-[6.875rem] w-[6.875rem] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(255,161,53,0.20), rgba(255,161,53,0))",
        }}
      />
      <div className="relative flex items-center gap-3">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[0.875rem] bg-primary/10">
          <TgEmoji char="💎" size={24} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[0.875rem] font-bold text-ink leading-tight">
            {kind === "days"
              ? `Подарунок +${bonus} днів`
              : kind === "trial"
                ? "Подарунок 14 днів"
                : "Знижка 55%"}
          </div>
          <div className="mt-1 text-[0.75rem] text-ink-dim leading-snug">
            {kind === "days"
              ? "Оформи тариф від місяця"
              : kind === "trial"
                ? "Два тижні за 1 ₴"
                : "Місяць за 449 ₴ замість 990"}
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-center rounded-[0.75rem] bg-primary/10 px-2.5 py-1.5">
          <span className="font-mono text-[0.5rem] uppercase tracking-wide text-primary/70 leading-none">
            залишилось
          </span>
          <span className="mt-0.5 font-display text-[0.9375rem] font-bold tabular-nums text-primary leading-none">
            {leftText}
          </span>
        </div>
      </div>
    </button>
  );
}

export function SubscriptionPage() {
  const navigate = useNavigate();
  const subQuery = useMySubscription();
  const tariffsQuery = useTariffs();
  const giftQuery = useGiftWindow();
  // Кнопка з бота веде сюди з ?gift=1 — одразу відкриваємо попап оффера.
  // Який саме подарунок відкритий у попапі (їх може бути кілька).
  const [giftOpen, setGiftOpen] = useState<string | null>(null);
  const autoOpen =
    new URLSearchParams(window.location.search).get("gift") === "1";
  // Мітка «людина дійшла до оффера з кнопки в боті» (owner 2026-09-01).
  // Ставимо один раз, коли сторінка відкрита саме з кнопки і подарунок живий.
  const markOpened = useMarkGiftOpened();
  const liveOffers = giftQuery.data?.offers?.length ?? 0;
  const markedRef = useRef(false);
  useEffect(() => {
    if (!autoOpen || liveOffers === 0 || markedRef.current) return;
    markedRef.current = true;
    markOpened.mutate();
  }, [autoOpen, liveOffers]);
  // Детект активації підписки + celebration-попап винесено в глобальний
  // <SubscriptionActivationWatcher /> (App root), щоб попап показувався на
  // будь-якому екрані, а не лише тут (юзер 2026-06-15).

  // Підписки немає, а гроші ще можуть списатись (owner 2026-10-19): тоді
  // «Автопродовження» лишається в управлінні й без підписки. Хук — до ранніх
  // виходів (інакше React #310).
  const autochargeQuery = useAutocharge(
    !!subQuery.data && !isActiveSubscription(subQuery.data),
  );

  if (subQuery.isLoading || tariffsQuery.isLoading) return <Spinner />;
  if (subQuery.isError)
    return <CenteredError message={subQuery.error.message} />;
  if (tariffsQuery.isError)
    return <CenteredError message={tariffsQuery.error.message} />;

  const sub = subQuery.data!;
  const tariffs = tariffsQuery.data!.items;
  const isActive = isActiveSubscription(sub);
  // Подарунків може бути кілька одночасно (owner 2026-08-11) — показуємо
  // плашку на кожен, а бейджі на тарифах беруть той, що дає найбільше днів.
  const offers = giftQuery.data?.offers ?? [];
  const gift = offers.find((o) => (o.bonus_days ?? 0) > 0) ?? offers[0];

  return (
    <div className="min-h-screen pb-16">
      <div className="px-5 pt-5">
        {/* Без активної підписки юзер потрапляє сюди з меню/пейволу (1 рівень)
            → лише «Меню». З активною — це керування з Профілю (2 рівні) →
            «Назад» + «Меню» (юзер 2026-06-14). */}
        {isActive ? (
          <BackNav onBack={() => navigate(-1)} showMenu />
        ) : (
          <BackNav />
        )}
      </div>

      {/* Hero — єдиний PageHeader для всіх розділів (owner 2026-08-11). */}
      <div className="px-5">
        <PageHeader
          eyebrow="підписка"
          title={isActive ? "Твоя підписка" : "Відкрий повний доступ"}
          titleLead={isActive ? "Твоя" : "Відкрий"}
          titleAccent={isActive ? "підписка." : "повний доступ."}
          scribbleWidth={isActive ? 130 : 220}
        />
      </div>

      <div className="px-5 mt-4">
        {offers.map((o) => (
          <GiftBanner key={o.offer} gift={o} onOpen={() => setGiftOpen(o.offer)} />
        ))}
        {isActive ? (
          <ActiveSubscriptionView sub={sub} tariffs={tariffs} gift={gift} />
        ) : (
          <InactiveSubscriptionView tariffs={tariffs} gift={gift} />
        )}
        {/* Підписки немає, а гроші ще можуть списатись (або стан невідомий —
            краще дати вимкнути, ніж сховати). */}
        {!isActive &&
          (autochargeQuery.data?.live || autochargeQuery.data?.unknown) && (
            <AutochargeOffBlock state={autochargeQuery.data} />
          )}
      </div>

      {(() => {
        const shown =
          offers.find((o) => o.offer === giftOpen) ??
          (autoOpen && giftOpen === null ? offers[0] : undefined);
        if (!shown) return null;
        return (
          <GiftOfferModal
            gift={shown}
            onClose={() => setGiftOpen("")}
            onGoToTariffs={(offer) => {
              // Лишаємось на цій же сторінці, але вмикаємо режим офера:
              // так подарунок працює ТІЛЬКИ після кнопки в попапі.
              setGiftOpen("");
              if (offer) {
                const url = new URL(window.location.href);
                url.searchParams.set("offer", offer);
                window.history.replaceState({}, "", url.toString());
                window.dispatchEvent(new PopStateEvent("popstate"));
              }
            }}
          />
        );
      })()}
    </div>
  );
}

// ============================================================
// View A — НЕТ активной подписки
// ============================================================

function InactiveSubscriptionView({
  tariffs,
  gift,
}: {
  tariffs: TariffOut[];
  gift?: GiftOfferOut;
}) {
  const invoiceMutation = useCreateInvoice();
  const referralsQuery = useReferrals();
  // Живі числа Бази знань для пункту «База знань» (owner 2026-09-27).
  const kbCounts = kbCountsFrom(useKBCounts().data);
  // ВАЖНО: для скидки беремо regular_balance_uah (бонуси за квести).
  // balance_uah — це referral cashback, він тільки для виводу.
  const refBalance = referralsQuery.data?.regular_balance_uah ?? 0;
  const [useBonus, setUseBonus] = useState(false);
  // Режим офера: сюди привела кнопка з попапа.
  const offerKey = offerFromUrl();
  const giftMode = !!offerKey && gift?.offer === offerKey;

  const handlePurchase = async (slug: string) => {
    const webapp = tg();
    let bonus = useBonus && !giftMode;
    if (bonus) {
      bonus = await ensureFreshBonusBalance(
        () => referralsQuery.refetch(),
        refBalance,
        (fresh) => {
          if (fresh <= 0) setUseBonus(false);
        },
      );
    }
    try {
      const result = await invoiceMutation.mutateAsync({
        tariff_slug: slug,
        use_bonus: bonus,
        ...(giftMode && offerKey ? { offer: offerKey } : {}),
      });
      // Юзер 2026-05-27: «після оплати хочу красивий pop з «підписку активовано».
      // Зберігаємо order_reference + timestamp в localStorage. SubscriptionPage
      // при mount/focus poll'ить /subscription/me — як тільки бачить нову
      // активну sub (started_at > pending_at) → показує celebration modal.
      localStorage.setItem(
        "pending_invoice",
        JSON.stringify({
          order_reference: result.order_reference,
          tariff_slug: slug,
          started_at_before: Date.now(),
        }),
      );
      if (webapp?.openLink) webapp.openLink(result.purchase_url);
      else window.open(result.purchase_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      webapp?.HapticFeedback?.notificationOccurred?.("error");
      showAppAlert(
        `Не вдалося створити рахунок: ${(e as Error).message}`,
      );
    }
  };

  // Скрываем недоступные тарифы (per владелец — не показывать серыми).
  // Backend возвращает available=true/false на основе истории юзера:
  // - Новые: только test + standart_1m
  // - После месяца: + 3m/6m/1y
  // - После 2 месяцев: + 1w
  const availableTariffs = tariffs.filter((t) => t.available);
  // Сортировка: test первым, затем по возрастанию цены
  const sortedTariffs = [...availableTariffs].sort((a, b) => {
    if (a.slug === "test") return -1;
    if (b.slug === "test") return 1;
    return parseFloat(a.amount) - parseFloat(b.amount);
  });

  return (
    <div className="space-y-4">
      {/* Selling hero block — продающие преимущества */}
      <div className="rounded-ios-hero bg-dark p-5 text-white shadow-ai-card relative overflow-hidden">
        <div
          className="pointer-events-none absolute -bottom-12 -right-12 h-[11.25rem] w-[11.25rem] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(255,161,53,0.40), rgba(255,161,53,0))",
          }}
        />
        <div className="relative">
          {/* Заголовок «З підпискою ти отримуєш все» прибрано (owner
              2026-08-26): він стояв одразу під hero «Відкрий повний доступ» —
              два заголовки поспіль, і другий нічого не додавав. */}
          <div className="space-y-3">
            {SUBSCRIPTION_BENEFITS.map((b) => (
              <BenefitRow
                key={b.title}
                icon={b.icon}
                title={b.title}
                desc={benefitDesc(b, kbCounts)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* TARIFFS — layout залежить від кількості доступних тарифів.
          Юзер 2026-05-24: «4 тарифи широкими блоками — куча пустого
          простору, погано. Для 1-2 тарифів треба окрема концепція бо
          інакше виглядає ще гірше».

          Логіка:
          - ≤2 тарифи (test + 1m, новий юзер): HERO-style картки
            full-width з продающими акцентами
          - 3+ тарифів: 2-col grid, всі однакового розміру, best-deal
            виділяється бейджем + цвітом, БЕЗ span-2 (не ламає сітку
            на парних N). */}
      <div>
        <div className="font-display text-[0.75rem] font-semibold uppercase tracking-[0.08em] text-ink-dim mb-2.5 px-1">
          Обери тариф
        </div>
        {sortedTariffs.length <= 2 ? (
          <div className="space-y-2.5">
            {sortedTariffs.map((t) => {
              const discount = useBonus && !giftMode
                ? calcBonusDiscount(parseFloat(t.amount), refBalance)
                : 0;
              return (
                <TariffHeroCard
                  key={t.slug}
                  tariff={t}
                  isBestDeal={t.slug === BEST_DEAL_SLUG}
                  isPending={
                    invoiceMutation.isPending &&
                    isInvoiceVarOfSlug(invoiceMutation.variables, t.slug)
                  }
                  bonusDiscount={discount}
                  giftDays={giftMode || !useBonus ? giftDaysFor(gift, t) : 0}
                  onClick={() => handlePurchase(t.slug)}
                />
              );
            })}
          </div>
        ) : (
          <div className="space-y-2.5">
            {/* Test-триал завжди full-width HERO-карткою (owner 2026-07-04),
                навіть коли показано всі тарифи (мігровані без активної
                підписки бачать test + 1m/3m/6m/12m) — щоб тестова не губилась
                дрібною карткою, а мала той самий вигляд, як на 2-тарифній
                сторінці нового юзера. Решта тарифів — у 2-col сітці. */}
            {sortedTariffs
              .filter((t) => t.slug === "test")
              .map((t) => {
                const discount = useBonus && !giftMode
                  ? calcBonusDiscount(parseFloat(t.amount), refBalance)
                  : 0;
                return (
                  <TariffHeroCard
                    key={t.slug}
                    tariff={t}
                    isBestDeal={t.slug === BEST_DEAL_SLUG}
                    isPending={
                      invoiceMutation.isPending &&
                      isInvoiceVarOfSlug(invoiceMutation.variables, t.slug)
                    }
                    bonusDiscount={discount}
                    giftDays={giftMode || !useBonus ? giftDaysFor(gift, t) : 0}
                    onClick={() => handlePurchase(t.slug)}
                  />
                );
              })}
            <div className="grid grid-cols-2 gap-2.5">
              {sortedTariffs
                .filter((t) => t.slug !== "test")
                .map((t) => {
                  const discount = useBonus && !giftMode
                    ? calcBonusDiscount(parseFloat(t.amount), refBalance)
                    : 0;
                  return (
                    <TariffCard
                      key={t.slug}
                      tariff={t}
                      isBestDeal={t.slug === BEST_DEAL_SLUG}
                      isPending={
                        invoiceMutation.isPending &&
                        isInvoiceVarOfSlug(invoiceMutation.variables, t.slug)
                      }
                      bonusDiscount={discount}
                      giftDays={giftMode || !useBonus ? giftDaysFor(gift, t) : 0}
                      onClick={() => handlePurchase(t.slug)}
                    />
                  );
                })}
            </div>
          </div>
        )}
        {/* BONUS toggle — під сіткою тарифів. Рендериться тільки якщо balance > 0. */}
        <BonusToggleBlock
          balance={refBalance}
          blockedByGift={giftMode}
          useBonus={useBonus}
          onToggle={setUseBonus}
        />
        {/* Немає бонусів → акуратна підказка як їх заробити на знижку
            (юзер 2026-06-11). Коли бонуси зʼявляються — замість цього
            показується BonusToggleBlock вище. */}
        {refBalance === 0 && <EarnBonusHint />}
      </div>
    </div>
  );
}

/** Підказка під тарифами для юзерів БЕЗ бонусів: заробляй на квестах →
 *  знижка на підписку. Веде у розділ квестів. */
function EarnBonusHint() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => {
        tg()?.HapticFeedback?.impactOccurred?.("light");
        navigate("/quests");
      }}
      className="mt-3 block w-full rounded-ios-card bg-surface p-4 text-left shadow-card transition active:scale-[0.99] active:bg-surface-soft"
    >
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[0.875rem] bg-primary/10 text-primary">
          <Icon name="star" size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[0.875rem] font-bold text-ink truncate">
            Заробляй бонуси
          </div>
          <div className="text-[0.75rem] text-ink-dim mt-0.5 leading-snug">
            Виконуй завдання — отримуй бонуси і сплачуй ними до 50% вартості
            підписки.
          </div>
        </div>
        <Icon name="arrow-right" size={14} color="#FE6B04" className="shrink-0" />
      </div>
    </button>
  );
}

/** invoiceMutation.variables може бути або CreateInvoiceArgs object, або
 *  legacy string. Helper повертає true якщо variables таргетить даний slug. */
function isInvoiceVarOfSlug(v: unknown, slug: string): boolean {
  if (typeof v === "string") return v === slug;
  if (v && typeof v === "object" && "tariff_slug" in v) {
    return (v as { tariff_slug: string }).tariff_slug === slug;
  }
  return false;
}

/**
 * Hero-варіант тарифу для випадків коли доступно 1-2 тарифи (новий юзер).
 * Full-width, з gradient backgrounds, продаючий tone.
 *
 * Test → emerald gradient + emoji 🎁 + «Спробуй за 1 ₴» CTA hint
 * 1m   → primary gradient + «Повний доступ»
 */
function TariffHeroCard({
  tariff,
  isBestDeal,
  isPending,
  bonusDiscount = 0,
  giftDays = 0,
  onClick,
}: {
  tariff: TariffOut;
  isBestDeal: boolean;
  isPending: boolean;
  bonusDiscount?: number;
  /** Скільки днів додасться в подарунок саме за цей тариф (owner 2026-08-11). */
  giftDays?: number;
  onClick: () => void;
}) {
  const isTest = tariff.slug === "test";
  const cleanTitle = cleanTariffTitle(tariff.title_uk);
  const perMonth = perMonthHint(tariff);
  const original = parseFloat(tariff.amount);
  const finalAmount = Math.max(0, original - bonusDiscount);
  const hasDiscount = bonusDiscount > 0;

  const skinClasses = isTest
    ? "bg-gradient-to-br from-emerald-50 via-emerald-100/50 to-emerald-50/30 border-2 border-emerald-300/60 shadow-card"
    : isBestDeal
      ? "bg-gradient-to-br from-primary/15 via-primary/8 to-primary/5 border-2 border-primary shadow-card"
      : "bg-surface border border-ink/8 shadow-card-soft";

  // Юзер 2026-05-24: «прибрати галочки — просто описом написати що
  // ця підписка дає». Дескриптивні підзаголовки замість bullet-списку.
  const description = isTest
    ? "7 днів повного доступу до вакансій за 1 ₴ — миттєві вакансії, AI-помічник для відгуків, контакти без лімітів і всі джерела."
    : "Повний доступ без обмежень: миттєві вакансії, AI-помічник для відповідей, контакти без лімітів, база знань і курс.";

  return (
    <button
      type="button"
      onClick={!isPending ? onClick : undefined}
      disabled={isPending}
      className={"relative w-full text-left rounded-ios-card p-5 transition active:scale-[0.98] " + skinClasses}
    >
      {isTest && (
        <div className="absolute -top-2 right-4 px-2.5 py-0.5 bg-emerald-500 text-white text-[0.625rem] font-display font-bold uppercase rounded-full tracking-wide">
          🎁 Спробуй
        </div>
      )}
      {isBestDeal && !isTest && (
        <div className="absolute -top-2 left-4 px-2.5 py-0.5 bg-primary text-white text-[0.625rem] font-display font-bold uppercase rounded-full tracking-wide">
          Найвигідніше
        </div>
      )}
      {giftDays > 0 && (
        /* Позначка подарунка саме там, де людина платить (owner 2026-08-11):
           повідомлення вона бачила день тому і вже не памʼятає умову. */
        <div className="absolute -top-2 right-4 px-2.5 py-0.5 bg-emerald-500 text-white text-[0.625rem] font-display font-bold rounded-full tracking-wide">
          +{giftDays} днів
        </div>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-display text-[1.25rem] font-bold text-ink leading-tight" style={{ letterSpacing: "-0.02em" }}>
            {cleanTitle}
          </div>
          {perMonth && (
            <div className="font-mono text-[0.6875rem] text-primary mt-1.5 font-semibold tracking-wide">
              {perMonth}
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          {isPending ? (
            <DotsLoader />
          ) : hasDiscount ? (
            <div className="flex flex-col items-end gap-0.5">
              <div className="font-mono text-[0.6875rem] text-ink-dim line-through tabular-nums">
                {formatAmount(tariff.amount)} ₴
              </div>
              <PriceBlock
                amount={String(finalAmount)}
                highlight={!isTest}
                size={32}
              />
              <div className="font-mono text-[0.625rem] text-emerald-600 font-bold tabular-nums">
                −{bonusDiscount} ₴ бонусами
              </div>
            </div>
          ) : (
            <PriceBlock amount={tariff.amount} highlight={!isTest} size={32} />
          )}
        </div>
      </div>

      {/* Description — одна цільна думка замість списку галочок */}
      <p className="mt-3 text-[0.8125rem] leading-relaxed text-ink-dim">
        {description}
      </p>

      {/* Тест: преміум-функції, недоступні на тест-підписці (юзер 2026-06-11) */}
      {isTest && (
        <div className="mt-3 rounded-xl border border-emerald-300/50 bg-white/60 p-3">
          <div className="mb-1.5 flex items-center gap-1.5 font-display text-[0.65625rem] font-bold uppercase tracking-wide text-emerald-700">
            <span className="text-[0.75rem] leading-none">🔒</span>
            <span className="leading-none">Не входить у тест</span>
          </div>
          <div className="text-[0.75rem] leading-relaxed text-ink-dim">
            <b className="text-ink">База знань</b> та{" "}
            <b className="text-ink">Курс від засновника</b> — преміум-функції,
            доступні лише на повній підписці.
          </div>
        </div>
      )}
    </button>
  );
}

function BenefitRow({
  icon,
  title,
  desc,
}: {
  icon: string;
  title: string;
  desc: string;
}) {
  return (
    <div className="flex items-start gap-3">
      {/* Висота іконки = висота рядка заголовка → emoji центрується саме
          по заголовку, а не «зависає» між title і desc (юзер 2026-06-08). */}
      <div className="flex h-5 shrink-0 items-center text-[1.25rem] leading-none">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-[0.84375rem] font-bold text-white leading-5">
          {title}
        </div>
        {/* Рядки опису однакової довжини, без короткого хвоста
            (owner 2026-09-27; UI-правило про багаторядковий текст). */}
        <div className="text-[0.71875rem] text-white/65 leading-snug mt-0.5 [text-wrap:balance]">
          {desc}
        </div>
      </div>
    </div>
  );
}

/**
 * Витягуємо «чисту» назву тарифу — БЕЗ дублюючої ціни.
 * Юзер 2026-05-24: «в назві ціна і знизу велика ціна — дубль некрасиво».
 *
 * Tariff titles мають форму:
 *   "1 місяць — 990 грн"     → "1 місяць"
 *   "3 місяці — 2391 грн"    → "3 місяці"
 *   "Тест-підписка 🚀"        → "Тест-підписка 🚀" (без зміни)
 *   "3 дні (30₴)"             → "3 дні"
 */
function cleanTariffTitle(rawTitle: string): string {
  // Strip " — NNN грн" або " — NNN ₴"
  let t = rawTitle.replace(/\s*[-—–]\s*\d[\d\s]*(?:грн|₴|UAH)\s*$/i, "").trim();
  // Strip " (NNN₴)" / " (NNN грн)"
  t = t.replace(/\s*\(\s*\d[\d\s]*(?:грн|₴|UAH)\s*\)\s*$/i, "").trim();
  // Strip ракету в кінці назви «Тест-підписка 🚀» (юзер 2026-06-14).
  t = t.replace(/\s*🚀\s*$/u, "").trim();
  return t;
}

/**
 * Per-month price hint для multi-month plans.
 * "1 місяць" / Test / Week → не показуємо (вже зрозуміло).
 * 3 / 6 місяців → показуємо «≈ X грн / міс».
 * 1 рік → 12 місяців → теж показуємо.
 *
 * Юзер 2026-05-24: «в річній підписці не написано вартість за місяць».
 */
function perMonthHint(tariff: TariffOut): string | null {
  let months: number | null = null;

  const monthsMatch = tariff.title_uk.match(/(\d+)\s*міс/i);
  if (monthsMatch) {
    months = parseInt(monthsMatch[1], 10);
  } else {
    // «1 рік» / «1 рок(у|ів)» → 12 місяців на одиницю року
    const yearsMatch = tariff.title_uk.match(/(\d+)\s*р(?:ік|ок|оки|оків)/i);
    if (yearsMatch) {
      months = parseInt(yearsMatch[1], 10) * 12;
    }
  }

  if (months === null || months < 2) return null;
  const amount = parseFloat(tariff.amount);
  if (!isFinite(amount) || amount <= 0) return null;
  const perMonth = Math.round(amount / months);
  return `≈ ${perMonth} грн / міс`;
}

/**
 * Compact grid tile (для 3+ тарифів). Всі тайли однакового розміру,
 * best-deal виділяється тільки бейджем + кольором (без span-2 — це
 * ламало grid на парних N тарифів).
 */
function TariffCard({
  tariff,
  isBestDeal,
  isPending,
  bonusDiscount = 0,
  giftDays = 0,
  onClick,
}: {
  tariff: TariffOut;
  isBestDeal: boolean;
  isPending: boolean;
  bonusDiscount?: number;
  giftDays?: number;
  onClick: () => void;
}) {
  const isTest = tariff.slug === "test";
  const cleanTitle = cleanTariffTitle(tariff.title_uk);
  const perMonth = perMonthHint(tariff);
  const original = parseFloat(tariff.amount);
  const finalAmount = Math.max(0, original - bonusDiscount);
  const hasDiscount = bonusDiscount > 0;

  const skinClasses = isBestDeal
    ? "bg-gradient-to-br from-primary/12 via-primary/5 to-primary/2 border-2 border-primary shadow-card"
    : isTest
      ? "bg-gradient-to-br from-emerald-50 to-emerald-100/40 border border-emerald-200 shadow-card-soft"
      : "bg-surface border border-ink/5 shadow-card-soft";

  return (
    <button
      type="button"
      onClick={!isPending ? onClick : undefined}
      disabled={isPending}
      className={
        "relative w-full h-full text-left rounded-ios-card p-3.5 transition active:scale-[0.98] flex flex-col " +
        skinClasses
      }
    >
      {isBestDeal && (
        <div className="absolute -top-2 left-3 px-2 py-0.5 bg-primary text-white text-[0.59375rem] font-display font-bold uppercase rounded-full tracking-wide">
          ★ Топ
        </div>
      )}
      {isTest && !isBestDeal && (
        <div className="absolute -top-2 left-3 px-2 py-0.5 bg-emerald-500 text-white text-[0.59375rem] font-display font-bold uppercase rounded-full tracking-wide">
          🎁 Спробуй
        </div>
      )}
      {giftDays > 0 && (
        <div className="absolute -top-2 right-3 px-2 py-0.5 bg-emerald-500 text-white text-[0.59375rem] font-display font-bold rounded-full tracking-wide">
          +{giftDays} днів
        </div>
      )}
      {/* Top: clean duration */}
      <div className="font-display text-[0.9375rem] font-bold text-ink leading-tight">
        {cleanTitle}
      </div>
      {perMonth && (
        <div className="font-mono text-[0.625rem] text-primary mt-1 font-semibold tracking-wide">
          {perMonth}
        </div>
      )}
      {/* Spacer push price to bottom */}
      <div className="flex-1 min-h-[0.625rem]" />
      {/* Bottom: big price */}
      {isPending ? (
        <DotsLoader />
      ) : hasDiscount ? (
        <div className="flex flex-col gap-0.5">
          <div className="font-mono text-[0.625rem] text-ink-dim line-through tabular-nums leading-none">
            {formatAmount(tariff.amount)} ₴
          </div>
          <PriceBlock
            amount={String(finalAmount)}
            highlight={isBestDeal}
            size={22}
          />
          <div className="font-mono text-[0.5625rem] text-emerald-600 font-bold tabular-nums leading-none mt-0.5">
            −{bonusDiscount} ₴ бонусами
          </div>
        </div>
      ) : (
        <PriceBlock
          amount={tariff.amount}
          highlight={isBestDeal}
          size={22}
        />
      )}
    </button>
  );
}

function DotsLoader() {
  return (
    <div className="font-display text-[1.125rem] font-bold text-ink leading-none">
      <span className="inline-flex items-center gap-0.5">
        <span className="animate-pulse">•</span>
        <span className="animate-pulse" style={{ animationDelay: "120ms" }}>•</span>
        <span className="animate-pulse" style={{ animationDelay: "240ms" }}>•</span>
      </span>
    </div>
  );
}

function PriceBlock({
  amount,
  highlight,
  size,
}: {
  amount: string;
  highlight: boolean;
  size: number;
}) {
  return (
    <div className="flex items-baseline gap-1.5">
      <div
        className={
          "font-display font-bold leading-none " +
          (highlight ? "text-primary" : "text-ink")
        }
        style={{ fontSize: size, letterSpacing: "-0.02em" }}
      >
        {formatAmount(amount)}
      </div>
      <div className="font-mono text-[0.625rem] uppercase tracking-[0.1em] text-ink-dim">
        грн
      </div>
    </div>
  );
}

// ============================================================
// View B — есть подписка. Управление.
// ============================================================

function ActiveSubscriptionView({
  sub,
  tariffs,
  gift,
}: {
  sub: SubscriptionOut;
  tariffs: TariffOut[];
  gift?: GiftOfferOut;
}) {
  // Лист бота «заморозку не оплачено» веде сюди з `?panel=freeze` (owner
  // 2026-10-20): одразу відкриваємо купівлю заморозки, а «Продовжити» згортаємо,
  // щоб панель не лишилась під згином екрана. На тесті заморозки немає.
  const freezeFromBot = sub.plan !== "test" && panelFromUrl() === "freeze";
  // «Продовжити підписку» відкрита одразу (юзер 2026-06-08).
  const [showExtend, setShowExtend] = useState(!freezeFromBot);
  const [showFreezeBuy, setShowFreezeBuy] = useState(freezeFromBot);
  const [showFreezeEarnedInput, setShowFreezeEarnedInput] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showCancelRetention, setShowCancelRetention] = useState(false);
  const freezeBalance = sub.freeze_days_balance ?? 0;
  // Заморозка — лише на платній підписці. На тесті — під замочком
  // (юзер 2026-06-07).
  const isTestSub = sub.plan === "test";

  const earnedFreezeMutation = useFreezeUseEarned();
  const [earnedDaysInput, setEarnedDaysInput] = useState("");

  // Панелі НЕЗАЛЕЖНІ: кожна відкривається/закривається лише своєю кнопкою
  // і лишається відкритою поки юзер сам її не закриє (юзер 2026-06-09:
  // «Продовжити підписку не має закриватись саме по собі коли відкриваю
  // інший розділ»). Раніше був accordion — відкриття однієї закривало інші.
  const togglePanel = (panel: "extend" | "freeze_buy" | "settings") => {
    if (panel === "extend") setShowExtend((v) => !v);
    else if (panel === "freeze_buy") setShowFreezeBuy((v) => !v);
    else if (panel === "settings") setShowSettings((v) => !v);
  };

  const handleFreezeFromBalance = async () => {
    const webapp = tg();
    const days = parseInt(earnedDaysInput, 10);
    if (Number.isNaN(days) || days <= 0) {
      showAppAlert("Введи число днів");
      return;
    }
    if (days > freezeBalance) {
      showAppAlert(`Максимум ${freezeBalance} дн.`);
      return;
    }
    try {
      const result = await earnedFreezeMutation.mutateAsync(days);
      webapp?.HapticFeedback?.notificationOccurred?.("success");
      showAppAlert(result.message);
      setShowFreezeEarnedInput(false);
      setEarnedDaysInput("");
    } catch (e) {
      webapp?.HapticFeedback?.notificationOccurred?.("error");
      showAppAlert(`Помилка: ${(e as Error).message}`);
    }
  };

  const isFrozen = sub.status === "frozen";
  const expiringSoon = !isFrozen && sub.days_left <= 7;
  // Залишок у днях + годинах (юзер 2026-06-08: «пиши ще й у годинах»).
  const msLeft = Math.max(0, new Date(sub.ends_at).getTime() - Date.now());
  const hoursTotal = Math.floor(msLeft / 3_600_000);
  // Під час заморозки ends_at стоїть на місці й може вже минути (owner
  // 2026-09-26): показуємо дні, що лишаться після розморозки, і дату, коли
  // підписка тоді скінчиться. Години на паузі не спливають — їх не пишемо.
  const daysLeftExact = isFrozen ? sub.days_left : Math.floor(hoursTotal / 24);
  const hoursLeftExact = isFrozen ? 0 : hoursTotal % 24;
  const endsAtShown = isFrozen ? (sub.ends_at_after_unfreeze ?? null) : sub.ends_at;

  return (
    <div className="space-y-3">
      {/* Ретеншн-офер (закреп із таймером) — якщо активний (юзер 2026-07-04) */}
      <RetentionOfferBanner />
      {/* HERO STATUS */}
      <div
        className={
          "relative overflow-hidden rounded-ios-card p-4 shadow-card-soft " +
          (isFrozen
            ? "bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200/50"
            : expiringSoon
              ? "bg-gradient-to-br from-orange-50 to-orange-100/50 border border-orange-200/50"
              : "bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200/50")
        }
      >
        <div className="flex items-start gap-3">
          <div
            className={
              "grid h-11 w-11 place-items-center rounded-[0.875rem] shrink-0 " +
              (isFrozen
                ? "bg-blue-500"
                : expiringSoon
                  ? "bg-orange-500"
                  : "bg-emerald-500")
            }
          >
            <Icon
              name={isFrozen ? "pause" : expiringSoon ? "clock" : "check"}
              size={20}
              color="#fff"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[0.625rem] uppercase tracking-[0.1em] text-ink-dim">
              {isFrozen
                ? "Заморожено"
                : expiringSoon
                  ? "Закінчується скоро"
                  : "Активна підписка"}
            </div>
            <div className="font-display text-[1.125rem] font-bold text-ink mt-0.5">
              {sub.plan === "test" ? "TEST" : "PRO"}
            </div>
            <div className="text-[0.75rem] text-ink-dim mt-0.5">
              {endsAtShown ? (
                <>
                  До <b className="text-ink">{formatDate(endsAtShown)}</b> ·{" "}
                  <b className="text-ink">
                    {daysLeftExact} дн.
                    {hoursLeftExact > 0 ? ` ${hoursLeftExact} год.` : ""}
                  </b>
                </>
              ) : (
                // Безстрокова заморозка: дати кінця ще не знає ніхто.
                <>
                  Лишиться <b className="text-ink">{daysLeftExact} дн.</b> після
                  розморозки
                </>
              )}
            </div>
          </div>
        </div>
        {expiringSoon && (
          <div className="mt-3 pt-3 border-t border-orange-200/40">
            <div className="text-[0.75rem] text-orange-900/80 leading-snug">
              ⚠️ Залишилось мало часу. Продовжи зараз — щоб не пропустити
              вакансії після закінчення.
            </div>
          </div>
        )}

        {/* Якщо frozen — інфо що підписка призупинена. Кнопка "Розморозити"
            прибрана (юзер 2026-05-22: «уберем — а то ломает логику»).
            Авто-розмороження через cron коли період закінчиться. */}
        {isFrozen && (
          <div className="mt-3 pt-3 border-t border-blue-200/40">
            {/* Owner 2026-08-10: людина бачила лише «заморожено» і залишок днів.
                Тепер прямо: на скільки заморожено, коли розмориться і скільки
                днів підписки лишиться після цього. */}
            {/* Owner 2026-08-10 (друга правка): було «полотно тексту». Тепер
                структура — мікро-заголовок + рядки «підпис → значення», як у
                решті карток. Значення жирні, підписи тонкі. */}
            <div className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-blue-900/50">
              Стан заморозки
            </div>
            <div className="mt-2 space-y-1.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[0.75rem] text-blue-900/60">Заморожено</span>
                <span className="text-[0.78125rem] font-semibold text-blue-900 tabular-nums">
                  {sub.frozen_days_total
                    ? `на ${sub.frozen_days_total} дн.`
                    : "без строку"}
                </span>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[0.75rem] text-blue-900/60">Розмориться</span>
                <span className="text-[0.78125rem] font-semibold text-blue-900 tabular-nums">
                  {sub.frozen_until
                    ? new Date(sub.frozen_until).toLocaleDateString("uk-UA", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })
                    : // Безстрокова заморозка від адміна — дати немає взагалі.
                      "зніме підтримка"}
                </span>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[0.75rem] text-blue-900/60">
                  Лишиться підписки
                </span>
                <span className="text-[0.78125rem] font-semibold text-blue-900 tabular-nums">
                  {daysLeftExact} дн.
                </span>
              </div>
            </div>
            <div className="mt-2 text-[0.6875rem] leading-snug text-blue-900/50 [text-wrap:balance]">
              {sub.frozen_until
                ? "Оплачені дні не згорають — відлік продовжиться після розморозки."
                : "Оплачені дні не згорають. Потрібно раніше — напиши в підтримку."}
            </div>
          </div>
        )}

        {/* Inline freeze-balance + freeze button у status card
            (юзер 2026-05-22: «в этой же таблетке писать сколько заморозки
            и добавить маленькую красивую кнопку заморозить»).
            Owner 2026-09-26: блок є і під час заморозки — тоді кнопка
            подовжує поточну паузу днями з балансу. Палітра — під колір
            картки (синя в заморозці, зелена без неї). */}
        <div
          className={
            "mt-3 pt-3 border-t " +
            (isFrozen ? "border-blue-200/40" : "border-emerald-200/40")
          }
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div
                className={
                  "text-[0.625rem] uppercase tracking-[0.08em] font-mono " +
                  (isFrozen ? "text-blue-900/55" : "text-emerald-900/55")
                }
              >
                Баланс заморозки
              </div>
              <div
                className={
                  "text-[0.8125rem] mt-0.5 " +
                  (isFrozen ? "text-blue-900" : "text-emerald-900")
                }
              >
                <b>{freezeBalance} дн.</b>{" "}
                <span
                  className={
                    "text-[0.6875rem] " +
                    (isFrozen ? "text-blue-800/60" : "text-emerald-800/60")
                  }
                >
                  (3 дн. / 30 днів)
                </span>
              </div>
            </div>
            {freezeBalance > 0 && !showFreezeEarnedInput && (
              <button
                type="button"
                onClick={() => setShowFreezeEarnedInput(true)}
                className={
                  "shrink-0 inline-flex items-center gap-1.5 rounded-full text-white px-3 py-1.5 font-display text-[0.71875rem] font-semibold leading-none transition " +
                  (isFrozen
                    ? "bg-blue-600 active:bg-blue-700"
                    : "bg-emerald-600 active:bg-emerald-700")
                }
              >
                <Icon name="pause" size={14} color="#fff" />
                <span className="leading-none">
                  {isFrozen ? "Подовжити" : "Заморозити"}
                </span>
              </button>
            )}
          </div>
          {showFreezeEarnedInput && (
            <div className="mt-3 flex gap-2 items-center">
              <input
                type="number"
                min={1}
                max={freezeBalance}
                value={earnedDaysInput}
                onChange={(e) => setEarnedDaysInput(e.target.value)}
                placeholder={`1 – ${freezeBalance}`}
                className={
                  "flex-1 rounded-xl bg-white/70 p-2.5 text-[0.8125rem] focus:outline-none focus:ring-2 " +
                  (isFrozen
                    ? "text-blue-900 placeholder:text-blue-800/40 focus:ring-blue-400"
                    : "text-emerald-900 placeholder:text-emerald-800/40 focus:ring-emerald-400")
                }
              />
              <button
                type="button"
                onClick={
                  earnedFreezeMutation.isPending
                    ? undefined
                    : handleFreezeFromBalance
                }
                disabled={earnedFreezeMutation.isPending}
                className={
                  "shrink-0 rounded-full text-white px-3.5 py-2 font-display text-[0.75rem] font-semibold transition disabled:opacity-50 " +
                  (isFrozen
                    ? "bg-blue-600 active:bg-blue-700"
                    : "bg-emerald-600 active:bg-emerald-700")
                }
              >
                {earnedFreezeMutation.isPending ? "..." : "OK"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowFreezeEarnedInput(false);
                  setEarnedDaysInput("");
                }}
                className={
                  "shrink-0 rounded-full bg-white/50 px-3 py-2 font-display text-[0.75rem] active:bg-white/80 transition " +
                  (isFrozen ? "text-blue-900" : "text-emerald-900")
                }
              >
                ✕
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ACTIONS — 3 кнопки рядами на повну ширину, з описом (юзер
          2026-06-08). Третя — одразу відкриває налаштування автопродовження.
          Owner 2026-09-26: і під час заморозки — все те саме, що й без неї:
          людина може продовжити підписку й докупити заморозку. */}
      <div className="flex flex-col gap-2">
        <ActionButton
          icon="renew"
          label="Продовжити підписку"
          sublabel="Додати днів до поточної"
          onClick={() => togglePanel("extend")}
          active={showExtend}
        />
        {/* Контент відкривається ОДРАЗУ під своєю кнопкою (юзер 2026-06-08) */}
        {showExtend && <ExtendPanel tariffs={tariffs} gift={gift} />}

        <ActionButton
          icon={isTestSub ? "lock" : "cart"}
          label="Купити заморозку"
          sublabel={
            isTestSub
              ? "Доступно з підпискою"
              : "Пауза підписки: 3 / 7 / 14 днів"
          }
          onClick={
            isTestSub
              ? () =>
                  showAppAlert(
                    "Заморозка доступна на платній підписці. Оформи місячну або довшу — і зможеш заморозити підписку.",
                  )
              : () => togglePanel("freeze_buy")
          }
          active={showFreezeBuy && !isTestSub}
        />
        {showFreezeBuy && !isTestSub && (
          <FreezeBuyPanel balanceBefore={freezeBalance} />
        )}

        <ActionButton
          icon="settings"
          label="Автопродовження"
          sublabel="Керування автопродовженням підписки"
          onClick={() => togglePanel("settings")}
          active={showSettings}
        />
        {showSettings && (
          <SettingsPanel
            sub={sub}
            onTryCancel={() => setShowCancelRetention(true)}
          />
        )}
      </div>

      {showCancelRetention && (
        <CancelRetentionModal
          sub={sub}
          onClose={() => setShowCancelRetention(false)}
        />
      )}
    </div>
  );
}

function ActionButton({
  icon,
  label,
  sublabel,
  active,
  onClick,
}: {
  icon: IconName;
  label: string;
  sublabel?: string;
  primary?: boolean;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "flex w-full items-center gap-3 rounded-ios-card p-4 text-left transition active:scale-[0.99] " +
        (active
          ? "bg-primary text-white shadow-ai-card"
          : "bg-surface text-ink shadow-card-soft")
      }
    >
      <div
        className={
          // Owner 2026-08-11: на цьому екрані всі плитки однакові — 44/14.
          "grid h-11 w-11 shrink-0 place-items-center rounded-[0.875rem] " +
          (active ? "bg-white/15" : "bg-primary/10")
        }
      >
        <Icon name={icon} size={20} color={active ? "#fff" : "#FE6B04"} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display text-[0.875rem] font-semibold leading-tight">
          {label}
        </div>
        {sublabel && (
          <div
            className={
              "text-[0.71875rem] mt-0.5 leading-snug " +
              (active ? "text-white/70" : "text-ink-dim")
            }
          >
            {sublabel}
          </div>
        )}
      </div>
      <div
        className={
          "shrink-0 transition-transform duration-200 " +
          (active ? "rotate-90" : "rotate-0")
        }
      >
        <Icon
          name="arrow-right"
          size={14}
          color={active ? "#FFFFFF" : "#FE6B04"}
        />
      </div>
    </button>
  );
}

function ExtendPanel({
  tariffs,
  gift,
}: {
  tariffs: TariffOut[];
  gift?: GiftOfferOut;
}) {
  const invoiceMutation = useCreateInvoice();
  const referralsQuery = useReferrals();
  // ВАЖНО: для скидки беремо regular_balance_uah (бонуси за квести).
  // balance_uah — це referral cashback, він тільки для виводу.
  const refBalance = referralsQuery.data?.regular_balance_uah ?? 0;
  const [useBonus, setUseBonus] = useState(false);
  // Режим офера: сюди привела кнопка з попапа.
  const offerKey = offerFromUrl();
  const giftMode = !!offerKey && gift?.offer === offerKey;
  const extendable = tariffs.filter((t) => t.slug !== "test" && t.available);

  const handlePurchase = async (slug: string) => {
    const webapp = tg();
    let bonus = useBonus && !giftMode;
    if (bonus) {
      bonus = await ensureFreshBonusBalance(
        () => referralsQuery.refetch(),
        refBalance,
        (fresh) => {
          if (fresh <= 0) setUseBonus(false);
        },
      );
    }
    try {
      const result = await invoiceMutation.mutateAsync({
        tariff_slug: slug,
        use_bonus: bonus,
        ...(giftMode && offerKey ? { offer: offerKey } : {}),
      });
      localStorage.setItem(
        "pending_invoice",
        JSON.stringify({
          order_reference: result.order_reference,
          tariff_slug: slug,
          started_at_before: Date.now(),
        }),
      );
      if (webapp?.openLink) webapp.openLink(result.purchase_url);
      else window.open(result.purchase_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      webapp?.HapticFeedback?.notificationOccurred?.("error");
      showAppAlert(
        `Не вдалося створити рахунок: ${(e as Error).message}`,
      );
    }
  };

  return (
    <div>
      <div className="font-display text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-ink-dim px-1 pt-1 mb-2">
        На скільки продовжити
      </div>
      {/* Той самий 2-col grid що і в InactiveSubscriptionView (юзер
          2026-05-24: «4 широких блока, куча пустого простору, поправ»). */}
      {extendable.length <= 2 ? (
        <div className="space-y-2.5">
          {extendable.map((t) => {
            const discount = useBonus && !giftMode
              ? calcBonusDiscount(parseFloat(t.amount), refBalance)
              : 0;
            return (
              <TariffHeroCard
                key={t.slug}
                tariff={t}
                isBestDeal={t.slug === BEST_DEAL_SLUG}
                isPending={
                  invoiceMutation.isPending &&
                  isInvoiceVarOfSlug(invoiceMutation.variables, t.slug)
                }
                bonusDiscount={discount}
                giftDays={giftMode || !useBonus ? giftDaysFor(gift, t) : 0}
                  onClick={() => handlePurchase(t.slug)}
              />
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2.5">
          {extendable.map((t) => {
            const discount = useBonus && !giftMode
              ? calcBonusDiscount(parseFloat(t.amount), refBalance)
              : 0;
            return (
              <TariffCard
                key={t.slug}
                tariff={t}
                isBestDeal={t.slug === BEST_DEAL_SLUG}
                isPending={
                  invoiceMutation.isPending &&
                  isInvoiceVarOfSlug(invoiceMutation.variables, t.slug)
                }
                bonusDiscount={discount}
                giftDays={giftMode || !useBonus ? giftDaysFor(gift, t) : 0}
                    onClick={() => handlePurchase(t.slug)}
              />
            );
          })}
        </div>
      )}
      <BonusToggleBlock
        balance={refBalance}
        blockedByGift={giftMode}
        useBonus={useBonus}
        onToggle={setUseBonus}
      />
    </div>
  );
}

/**
 * FreezeBuyPanel — КУПИТИ окремий freeze-тариф (3/7/14 днів).
 * Юзер 2026-05-22: «купить заморозку — это отдельная функция».
 */
function FreezeBuyPanel({ balanceBefore }: { balanceBefore: number }) {
  const tariffsQuery = useFreezeTariffs();
  // ЗМІНА (owner 2026-07-22): оплата заморозки БІЛЬШЕ НЕ заморожує підписку
  // одразу — вебхук НАРАХОВУЄ дні на баланс заморозки. Юзер застосовує їх
  // сам, коли зручно. Після повернення з WFP FreezeCreditedWatcher (App)
  // детектить ріст балансу по pending_freeze_invoice і показує попап
  // «Заморозку нараховано».
  const invoiceMutation = useFreezeInvoice();

  const handleFreeze = async (slug: string, days: number, amount: string) => {
    const webapp = tg();
    const confirmed = window.confirm(
      `Купити заморозку на ${days} днів за ${amount} ₴? Після оплати дні нарахуються на баланс заморозки — застосуєш їх коли зручно.`,
    );
    if (!confirmed) return;
    try {
      const result = await invoiceMutation.mutateAsync(slug);
      // pending_freeze_invoice → FreezeCreditedWatcher покаже попап, щойно
      // freeze_days_balance виросте відносно balance_before. НЕ pending_invoice
      // (той ключ — для активації ПІДПИСКИ; заморозка її більше не чіпає).
      localStorage.setItem(
        "pending_freeze_invoice",
        JSON.stringify({
          order_reference: result.order_reference,
          days,
          balance_before: balanceBefore,
          started_at_before: Date.now(),
        }),
      );
      if (webapp?.openLink) webapp.openLink(result.purchase_url);
      else window.open(result.purchase_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      webapp?.HapticFeedback?.notificationOccurred?.("error");
      showAppAlert(
        `Не вдалося створити рахунок: ${(e as Error).message}`,
      );
    }
  };

  if (tariffsQuery.isLoading) return <Spinner />;
  if (tariffsQuery.isError) return null;
  const items = tariffsQuery.data?.items ?? [];

  return (
    <div>
      <div className="font-display text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-ink-dim px-1 pt-1 mb-2">
        Купити окрему заморозку
      </div>
      {/* 3-col grid — кожен freeze тариф з blue-gradient + snowflake icon.
          Юзер 2026-05-24: «три білі картки скучно» — даємо колір. */}
      <div className="grid grid-cols-3 gap-2">
        {items.map((t, idx) => {
          const cleanTitle = cleanTariffTitle(t.title_uk);
          // Кожний tier — трохи інтенсивніший gradient (3д → 7д → 14д)
          const intensities = [
            "from-blue-50 to-blue-100/60 border-blue-200",
            "from-cyan-50 to-blue-100/60 border-cyan-200",
            "from-sky-50 to-cyan-100/60 border-sky-200",
          ];
          const gradientClass = intensities[idx] || intensities[0];
          return (
            <button
              type="button"
              key={t.slug}
              onClick={
                invoiceMutation.isPending
                  ? undefined
                  : () => handleFreeze(t.slug, t.days, t.amount)
              }
              disabled={invoiceMutation.isPending}
              className={
                "w-full text-left rounded-ios-card shadow-card-soft p-3 active:scale-[0.98] transition flex flex-col items-start bg-gradient-to-br border " +
                gradientClass
              }
            >
              {/* Snowflake icon → seamless «freeze» visual cue */}
              <div className="grid h-7 w-7 place-items-center rounded-full bg-white/70 mb-2">
                <Icon name="snowflake" size={14} color="#0284c7" />
              </div>
              <div className="font-display text-[0.8125rem] font-bold text-ink leading-tight">
                {cleanTitle}
              </div>
              <div className="flex-1 min-h-[0.25rem]" />
              <div className="flex items-baseline gap-1 mt-1.5">
                <div
                  className="font-display font-bold leading-none"
                  style={{
                    fontSize: "1.125rem",
                    letterSpacing: "-0.02em",
                    color: "#0369a1",
                  }}
                >
                  {formatAmount(t.amount)}
                </div>
                <div className="font-mono text-[0.59375rem] uppercase tracking-[0.08em] text-ink-dim">
                  грн
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SettingsPanel({
  sub,
  onTryCancel,
}: {
  sub: SubscriptionOut;
  onTryCancel: () => void;
}) {
  return (
    <div className="rounded-ios-card bg-surface shadow-card-soft overflow-hidden">
      <button
        type="button"
        onClick={
          // Заморожена теж: вимкнути автопродовження можна й на паузі
          // (owner 2026-09-26).
          sub.is_recurring &&
          (sub.status === "active" || sub.status === "frozen")
            ? onTryCancel
            : undefined
        }
        className="w-full text-left p-4 active:bg-surface-soft transition"
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-display text-[0.8125rem] font-semibold text-ink">
              Автопродовження
            </div>
            <div className="text-[0.6875rem] text-ink-dim mt-0.5">
              {sub.is_recurring
                ? "Підписка продовжиться автоматично"
                : "Списання не відбудеться"}
            </div>
          </div>
          <div
            className={
              "shrink-0 px-2.5 py-1 rounded-full text-[0.625rem] font-display font-bold uppercase " +
              (sub.is_recurring
                ? "bg-emerald-100 text-emerald-700"
                : "bg-ink/5 text-ink-dim")
            }
          >
            {sub.is_recurring ? "Увімкнено" : "Вимкнено"}
          </div>
        </div>
      </button>
    </div>
  );
}

/** Попап підтвердження «Вимкнути автопродовження?» без підписки (він же — у
 *  галереї попапів). */
export function confirmAutochargeOff(onConfirm: () => void) {
  showAppAlert(
    "Після вимкнення ми більше не списуватимемо оплату. Оформити підписку знову можна будь-коли.",
    {
      title: "Вимкнути автопродовження?",
      emoji: "💸",
      buttons: [
        { label: "Вимкнути", variant: "danger", onClick: onConfirm },
        { label: "Скасувати", variant: "ghost" },
      ],
    },
  );
}

/**
 * «Автопродовження» без діючої підписки (owner 2026-10-19): підписка
 * скінчилась, а гроші ще можуть списатись — ідуть повтори списання, тест
 * добіг кінця. Той самий пункт і той самий вигляд, що й в управлінні
 * діючою підпискою; вимкнення — після підтвердження.
 */
function AutochargeOffBlock({ state }: { state: AutochargeOut }) {
  const [open, setOpen] = useState(false);
  const cancelMutation = useCancelRecurring();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: queryKeys.autocharge });

  const turnOff = async () => {
    const webapp = tg();
    try {
      const result = await cancelMutation.mutateAsync();
      webapp?.HapticFeedback?.notificationOccurred?.("success");
      showAppAlert(result.message);
      setOpen(false);
    } catch (e) {
      // «Уже вимкнено» (404) — не помилка: просто оновлюємо стан, і пункт
      // зникне. Будь-яка інша — чесно кажемо й перезапитуємо стан: частину
      // (скажімо, monobank) могли вже вимкнути.
      if (e instanceof ApiError && e.status === 404) {
        showAppAlert(e.detail);
      } else {
        webapp?.HapticFeedback?.notificationOccurred?.("error");
        showAppAlert(
          `Не вдалося вимкнути: ${e instanceof ApiError ? e.detail : (e as Error).message}`,
        );
      }
      void refresh();
    }
  };

  const confirm = () => {
    tg()?.HapticFeedback?.impactOccurred?.("light");
    confirmAutochargeOff(() => void turnOff());
  };

  return (
    <Section title="Управління підпискою" className="mt-6">
      <div className="flex flex-col gap-2">
        <ActionButton
          icon="settings"
          label="Автопродовження"
          sublabel="Керування автопродовженням підписки"
          onClick={() => setOpen((v) => !v)}
          active={open}
        />
        <Collapse open={open}>
          <div className="rounded-ios-card bg-surface shadow-card-soft overflow-hidden">
            <button
              type="button"
              onClick={cancelMutation.isPending ? undefined : confirm}
              disabled={cancelMutation.isPending}
              className="w-full text-left p-4 active:bg-surface-soft transition"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="font-display text-[0.8125rem] font-semibold text-ink">
                  Автопродовження
                </div>
                {/* Стан невідомий (платіжна система не відповіла) — не
                    стверджуємо «Увімкнено», але вимкнути дозволяємо. */}
                {state.live && (
                  <div className="shrink-0 px-2.5 py-1 rounded-full text-[0.625rem] font-display font-bold uppercase bg-emerald-100 text-emerald-700">
                    {cancelMutation.isPending ? "…" : "Увімкнено"}
                  </div>
                )}
              </div>
            </button>
          </div>
        </Collapse>
      </div>
    </Section>
  );
}

/**
 * «Шкода що йдеш…» — перший і ЄДИНИЙ крок вимкнення автопродовження.
 *
 * Owner 2026-10-22: «Все одно вимкнути» вимикає ОДРАЗУ. Раніше ця кнопка
 * лише відкривала спецпропозицію, а вимикала автопродовження тільки кнопка в
 * ній — хто просто закривав пропозицію, лишався з автопродовженням і
 * отримував списання 990 ₴ наступного дня (кейс @OlenaUnharova). Тепер офер
 * приходить у відповіді сервера ВЖЕ ПІСЛЯ вимкнення і показується станом
 * «Автопродовження вимкнено, але спецпропозиція ще діє» — закрити його =
 * нічого не змінити. Збій — чесна помилка, і рядок показує справжній стан.
 */
export function CancelRetentionModal({
  sub,
  onClose,
  preview = false,
}: {
  sub: SubscriptionOut;
  onClose: () => void;
  /** Галерея попапів: жодних запитів, кнопки лише закривають попап. */
  preview?: boolean;
}) {
  const cancelMutation = useCancelRecurring();
  const isTest = sub.plan === "test";

  const handleTurnOff = async () => {
    if (preview) {
      onClose();
      return;
    }
    const webapp = tg();
    webapp?.HapticFeedback?.impactOccurred?.("light");
    try {
      const result = await cancelMutation.mutateAsync();
      webapp?.HapticFeedback?.notificationOccurred?.("success");
      onClose();
      // Вимкнення підтверджене сервером. Є офер — показуємо його станом
      // «вимкнено, але пропозиція ще діє»; немає — просто кажемо «вимкнено».
      if (result.retention_offer?.available) {
        openRetentionOffer("reminder");
      } else {
        showAppAlert(result.message);
      }
    } catch (e) {
      // Не «вимкнено», поки сервер цього не підтвердив: закриваємо попап,
      // рядок «Автопродовження» перезапитується й показує справжній стан.
      webapp?.HapticFeedback?.notificationOccurred?.("error");
      onClose();
      showAppAlert(
        e instanceof ApiError && e.status === 502 && e.detail
          ? e.detail
          : "Не вдалося вимкнути автопродовження. Спробуй ще раз за хвилину.",
      );
    }
  };

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        height:
          typeof window !== "undefined" &&
          (window as any).Telegram?.WebApp?.viewportStableHeight
            ? `${(window as any).Telegram.WebApp.viewportStableHeight}px`
            : "100vh",
      }}
      onClick={cancelMutation.isPending ? undefined : onClose}
    >
      <div
        className="w-full max-w-md bg-surface rounded-3xl p-5 shadow-2xl overflow-y-auto"
        style={{ maxHeight: "calc(100% - 2rem)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center mb-5">
          <div className="mb-3 flex justify-center">
            <TgEmoji char="😔" size={48} />
          </div>
          <div className="font-display text-[1.25rem] font-bold text-ink leading-tight">
            Шкода що йдеш...
          </div>
          <div className="text-[0.8125rem] text-ink-dim mt-2 leading-relaxed [text-wrap:balance]">
            {isTest ? (
              <>
                Без автопродовження після тестового періоду ти знов потрапиш
                на FREE — з затримкою 4 години і лімітом 5 контактів/тиждень.
              </>
            ) : (
              <>
                Без автопродовження після{" "}
                <b>{formatDate(sub.ends_at_after_unfreeze ?? sub.ends_at)}</b> ти
                втратиш миттєвий доступ до вакансій і відкриті контакти.
              </>
            )}
          </div>
        </div>

        <div className="rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 p-4 mb-5">
          <div className="font-display text-[0.8125rem] font-bold text-ink mb-2">
            Що ти втратиш:
          </div>
          <ul className="space-y-1 text-[0.75rem] text-ink/80">
            <li>⚡ Миттєву доставку вакансій</li>
            <li>👁️ Відкриті контакти без лімітів</li>
            <li>🤖 AI-помічник для генерації відгуків</li>
            <li>📚 Доступ до бази знань та курсу</li>
            <li>🚀 Швидку підтримку</li>
          </ul>
        </div>

        <div className="space-y-2">
          <MainButton
            onClick={onClose}
            disabled={cancelMutation.isPending}
          >
            Залишити автопродовження
          </MainButton>
          <MainButton
            tone="secondary"
            onClick={handleTurnOff}
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending ? "Вимикаю…" : "Все одно вимкнути"}
          </MainButton>
        </div>
      </div>
    </div>
  );
  return createPortal(modal, document.body);
}

// ============================================================
// BONUS-toggle блок (юзер 2026-05-25)
// «Під тарифами блок: на балансі N бонусів, тумблер «використати бонуси» —
//  при увімкненні кожен тариф показує знижку (макс. min(стеля, balance))».
// ⚠️ Стеля — ОДНА цифра на весь застосунок: `MAX_BONUS_SHARE` у lib/bonuses.ts.
// Owner 2026-09-05: на сервері підняли до 50%, а тут лишалось 30 — людина
// бачила «до 30%» і знижку 90 ₴ там, де мала бути 150.
// ============================================================

/** Знижка бонусами на тариф = min(стеля від ціни, поточний баланс юзера). */
export function calcBonusDiscount(
  tariffAmount: number,
  balance: number,
): number {
  const max = Math.floor(tariffAmount * MAX_BONUS_SHARE);
  return Math.min(max, balance);
}

export function BonusToggleBlock({
  balance,
  useBonus,
  onToggle,
  blockedByGift = false,
}: {
  balance: number;
  useBonus: boolean;
  onToggle: (v: boolean) => void;
  /** Живий подарунок: сервер бонуси не прийме, тож вимикач недоступний. */
  blockedByGift?: boolean;
}) {
  if (balance <= 0) return null;
  if (blockedByGift) {
    return (
      <div className="mt-4 rounded-ios-card bg-surface shadow-card p-4">
        <div className="font-display text-[0.9375rem] font-bold text-ink">
          На балансі: {balance} ₴ бонусів
        </div>
        <div className="mt-1 text-[0.8125rem] leading-snug text-ink-dim [text-wrap:balance]">
          Поки діє подарунок, оплатити бонусами не вийде — вони чекатимуть
          наступного разу.
        </div>
      </div>
    );
  }
  return (
    <div className="mt-4 rounded-ios-card bg-surface shadow-card overflow-hidden">
      <button
        type="button"
        onClick={() => onToggle(!useBonus)}
        className="w-full flex items-center gap-3 p-4 active:bg-surface-soft transition text-left"
      >
        <div className="grid h-10 w-10 place-items-center rounded-[0.75rem] shrink-0 bg-primary/10">
          <Icon name="star" size={16} color="#FE6B04" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[0.8125rem] font-semibold text-ink-dim leading-tight">
            На балансі:
          </div>
          <div className="font-display text-[1rem] font-bold text-ink leading-tight mt-0.5">
            <span className="text-primary tabular-nums">{balance} ₴</span> бонусів
          </div>
          <div className="text-[0.8125rem] mt-1.5 leading-snug text-ink-dim">
            Можеш оплатити <b className="text-ink">до {MAX_BONUS_PERCENT}%</b> вартості
            будь-якої підписки бонусами
          </div>
        </div>
        {/* iOS-style toggle */}
        <div
          className={
            "relative h-7 w-12 shrink-0 rounded-full transition " +
            (useBonus ? "bg-primary" : "bg-ink/15")
          }
        >
          <div
            className={
              "absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-md transition-all " +
              (useBonus ? "left-[1.375rem]" : "left-0.5")
            }
          />
        </div>
      </button>
    </div>
  );
}

